import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Button from '../../components/ui/Button';
import GenericSelect from '../../components/ui/GenericSelect';
import AsyncGenericSelect from '../../components/ui/AsyncGenericSelect';
import PmAnalyticsFilterBar from '../../components/performance/PmAnalyticsFilterBar';
import usePmAnalyticsFilters, { latestPeriod, resolveComparePeriod } from '../../components/performance/pmAnalyticsFilters';
import PanelCard from '../performance-operations/components/PanelCard';
import {
  Grid, KpiSub, KpiValue, PageEmpty, PageWrap,
} from '../performance-operations/styles';
import {
  anyStale, fetchPeriods, oldestCalculatedAt, usePanel,
} from '../performance-operations/config';
import { TAB_TASKS, processHealthHref } from '../../components/performance/processHealthLinks';
import {
  DEFAULT_HEATMAP_CODES, GROUP_BYS, MIN_N_OPTIONS, Q, UNIT_METRIC_CODES, downloadBlob, exportUnits, fetchUnits, formatNum, metaOf,
  metricByCode, metricText, orgComparisonLink, resultsLink, unitLabel, usePageParams,
} from '../performance-results/config';
import UnitHeatmap from './components/UnitHeatmap';
import UnitTable from './components/UnitTable';
import UnitDetailDrawer from './components/UnitDetailDrawer';

// PM analitik P2 — E10 Organizasyon Karşılaştırmaları (/performance/org-comparison). Üst yönetim ve İK için sorunun hangi birimde
// yoğunlaştığını gösterir (yalnız İK: analiz §2 "üst yönetim E01 + E10"; HRBP kapsamı sonraki faz). Gruplama: yönetici
// hiyerarşisi (şirket → direktörlük → departman → ekip; yol kırıntısı + Geri) ya da düz boyut (departman, lokasyon, ünvan).
// Şirket/başlık değeri kişi düzeyinden hesaplanır; iç içe birimler çift sayılmaz; küçük grup ve tamamlayıcı hücreler gizlenir.
// Anlamlılık ifadesi kullanılmaz — karşılaştırma açık eşik ve kapsamla. Süzgeç + seçimler URL'de; birim çekmecesi ?focus=.
const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px;
  margin-bottom: 12px;
`;

const Field = styled.div`
  width: ${({ $w }) => $w || '220px'};
  max-width: 100%;
  @media (max-width: 768px) { width: 100%; }
`;

const PathBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  & > nav { margin-bottom: 0; }
  margin-bottom: 12px;
`;

const HEADER_CODES = ['M01', 'M02', 'M06', 'M09'];

export default function PerformanceOrgComparisonPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const history = useHistory();
  const { filters, setFilters, reset } = usePmAnalyticsFilters();
  const [params, setParams] = usePageParams([Q.groupBy, Q.parent, Q.metrics, Q.minN, Q.focus]);
  const [periods, setPeriods] = useState([]);
  const [periodsLoaded, setPeriodsLoaded] = useState(false);
  const [metas, setMetas] = useState({});
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    let alive = true;
    fetchPeriods().then((list) => { if (alive) { setPeriods(list); setPeriodsLoaded(true); } });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!periodsLoaded || periods.length === 0) return;
    if (!filters.periodId || !periods.some((p) => p.id === filters.periodId)) {
      setFilters((f) => ({ ...f, periodId: latestPeriod(periods).id }));
    }
  }, [periodsLoaded, periods, filters.periodId, setFilters]);

  const filterKey = JSON.stringify(filters);
  useEffect(() => { setMetas({}); }, [filterKey]);
  const onMeta = useCallback((key, meta) => setMetas((m) => ({ ...m, [key]: meta })), []);

  const groupBy = GROUP_BYS.includes(params[Q.groupBy]) ? params[Q.groupBy] : 'hierarchy';
  const parent = groupBy === 'hierarchy' ? params[Q.parent] || null : null;
  const minN = MIN_N_OPTIONS.includes(Number(params[Q.minN])) ? Number(params[Q.minN]) : 0;
  const codes = useMemo(() => {
    const picked = (params[Q.metrics] || '').split(',').filter((c) => UNIT_METRIC_CODES.includes(c));
    return picked.length ? picked : DEFAULT_HEATMAP_CODES;
  }, [params]);
  const comparePeriod = useMemo(() => resolveComparePeriod(periods, filters), [periods, filters]);
  // URL'deki dönem listede yoksa (bayat yer imi) paneller o dönemle istek atmaz; dönem etkisi en yeni döneme geçirir.
  const ready = periodsLoaded && periods.some((p) => p.id === filters.periodId);

  const units = usePanel(
    () => fetchUnits(filters, { groupBy, parentEmployeeId: parent, minN, comparePeriodId: comparePeriod?.id }),
    [filterKey, groupBy, parent, minN, comparePeriod?.id],
    ready,
  );
  useEffect(() => { if (units.data) onMeta('units', metaOf(units.data)); }, [units.data, onMeta]);
  const data = units.data;

  // Birim çekmecesi URL'de (?focus=): E01 organizasyon satırından ve E03 organizasyon hücresinden doğrudan açılır.
  const focusKey = params[Q.focus];
  const focusUnit = useMemo(() => {
    if (!focusKey) return null;
    return (data?.units || []).find((u) => u.key === focusKey) || { key: focusKey, kind: groupBy };
  }, [focusKey, data, groupBy]);

  const openUnit = (u) => setParams({ [Q.focus]: u.key });
  const drill = (u) => setParams({ [Q.parent]: u.key, [Q.focus]: null }, { push: true });
  const back = () => {
    const path = data?.path || [];
    const up = path.length >= 2 ? path[path.length - 2].key : null;
    setParams({ [Q.parent]: up, [Q.focus]: null }, { push: true });
  };

  // Birimden kişi listesine (E03) iniş: İK'nın bireysel erişimi var; süzgeç birimi tanımlar.
  const employeesHref = (u) => {
    if (!u) return null;
    if (u.kind === 'department' && u.key) return resultsLink({ ...filters, departmentIds: [u.key] });
    if (u.kind === 'title' && u.key) return resultsLink({ ...filters, titleIds: [u.key] });
    if (u.kind === 'location' && u.key) return resultsLink({ ...filters, locationIds: [u.key] });
    if (u.kind === 'manager' && u.key) return resultsLink({ ...filters, managerEmployeeId: u.key, includeDescendants: true });
    if (u.kind === 'direct' && data?.parentKey) return resultsLink({ ...filters, managerEmployeeId: data.parentKey, includeDescendants: false });
    return null;
  };
  const tasksHref = (u) => {
    if (!u) return null;
    // E02 Görevler, ortak süzgeçler + bu birim + gecikmiş (Plan 4 bağlantı sözleşmesi, spec §19/4).
    if (u.kind === 'department' && u.key) return processHealthHref(filters, { tab: TAB_TASKS, departmentIds: [u.key], task: { overdue: 'true' } });
    if (u.kind === 'manager' && u.key) {
      return processHealthHref({ ...filters, managerEmployeeId: u.key, includeDescendants: true }, { tab: TAB_TASKS, task: { overdue: 'true' } });
    }
    return null;
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await exportUnits(filters, { groupBy, parentEmployeeId: parent, minN, comparePeriodId: comparePeriod?.id });
      downloadBlob(res, 'organizasyon-karsilastirma.xlsx');
    } finally {
      setExporting(false);
    }
  };

  const groupOptions = GROUP_BYS.map((g) => ({ id: g, name: t(`performance.results.analytics.groupBy.${g}`) }));
  const minNOptions = MIN_N_OPTIONS.map((n) => ({ id: String(n), name: n === 0 ? t('performance.orgComparison.minN.none') : t('performance.orgComparison.minN.value', { n }) }));
  const metricLookup = useMemo(() => ({
    onSearch: async () => UNIT_METRIC_CODES.map((c) => ({ id: c, name: t(`performance.results.analytics.metric.${c}`) })),
    onGetById: async (id) => ({ id, name: t(`performance.results.analytics.metric.${id}`) }),
  }), [t]);

  const pathItems = (data?.path || []).map((p, i, all) => ({
    label: p.key ? (p.label || '-') : t('performance.results.analytics.unit.company'),
    path: i === all.length - 1 ? undefined
      : orgComparisonLink(filters, { [Q.groupBy]: groupBy, [Q.parent]: p.key, [Q.metrics]: params[Q.metrics], [Q.minN]: params[Q.minN] }),
  }));

  return (
    <PageWrap>
      <Breadcrumbs items={[{ label: t('performance.orgComparison.title') }]} />
      {periodsLoaded && periods.length === 0 ? <PageEmpty data-state="no-periods">{t('performance.results.analytics.noPeriods')}</PageEmpty> : null}
      {periods.length > 0 ? (
        <PmAnalyticsFilterBar
          periods={periods}
          filters={filters}
          onChange={setFilters}
          onReset={reset}
          lastUpdated={oldestCalculatedAt(metas)}
          stale={anyStale(metas)}
          groupMembership={filters.groupIds.length ? 'current' : null}
        />
      ) : null}
      {ready ? (
        <Controls>
          <Field>
            <GenericSelect
              label={t('performance.orgComparison.groupBy')}
              value={groupBy}
              options={groupOptions}
              clearable={false}
              onChange={(v) => setParams({ [Q.groupBy]: v === 'hierarchy' ? null : v, [Q.parent]: null, [Q.focus]: null })}
            />
          </Field>
          <Field $w="180px">
            <GenericSelect
              label={t('performance.orgComparison.minN.label')}
              value={String(minN)}
              options={minNOptions}
              clearable={false}
              onChange={(v) => setParams({ [Q.minN]: v && v !== '0' ? v : null })}
            />
          </Field>
          <Field $w="320px">
            <AsyncGenericSelect
              label={t('performance.orgComparison.metrics')}
              value={codes}
              isMulti
              minChars={0}
              compact
              onChange={(v) => setParams({ [Q.metrics]: (v || []).length ? v.join(',') : null })}
              {...metricLookup}
            />
          </Field>
        </Controls>
      ) : null}
      {ready && groupBy === 'hierarchy' && data ? (
        <PathBar data-path="1">
          {parent ? (
            <Button variant="outline" onClick={back} data-action="back">
              <ArrowLeft size={14} />
              {' '}
              {t('performance.orgComparison.back')}
            </Button>
          ) : null}
          <Breadcrumbs items={pathItems} />
        </PathBar>
      ) : null}
      {ready ? (
        <Grid key={filters.periodId}>
          {HEADER_CODES.map((code) => {
            const m = data ? metricByCode(data.header.metrics, code) : null;
            return (
              <PanelCard
                key={code} panelKey={`header-${code}`} span={3} compact
                title={t(`performance.results.analytics.kpi.${code}`)}
                loading={units.loading} error={units.error} onRetry={units.retry}
              >
                <KpiValue data-kpi={code}>{metricText(t, m, lang)}</KpiValue>
                <KpiSub>
                  {data ? `${unitLabel(t, data.header)}${m && m.sampleN !== null && m.sampleN !== undefined && m.kind === 'result' ? ` · n = ${formatNum(m.sampleN, lang, 0)}` : ''}` : null}
                </KpiSub>
              </PanelCard>
            );
          })}
          <UnitHeatmap data={data} loading={units.loading} error={units.error} onRetry={units.retry} codes={codes} onOpen={openUnit} />
          <UnitTable
            data={data} loading={units.loading} onOpen={openUnit} onDrill={drill}
            employeesLink={(u) => { const href = employeesHref(u); return href ? () => history.push(href) : null; }}
            onExport={handleExport} exporting={exporting}
          />
        </Grid>
      ) : null}
      <UnitDetailDrawer
        open={!!focusUnit}
        onClose={() => setParams({ [Q.focus]: null })}
        filters={filters}
        groupBy={groupBy}
        parentKey={parent}
        unit={focusUnit}
        employeesHref={employeesHref(focusUnit)}
        tasksHref={tasksHref(focusUnit)}
        onDrill={groupBy === 'hierarchy' ? drill : null}
      />
    </PageWrap>
  );
}
